import { useTranslation } from 'react-i18next';

import { Chip } from '@mui/material';

import {
  BONUS_STATUS_LABELS,
  CARD_POOL_STATUS_LABELS,
  CARD_STATUS_LABELS,
  CARD_TYPE_LABELS,
  PROGRAM_MODEL_LABELS,
  RESERVATION_STATUS_LABELS,
  RULE_TYPE_LABELS,
  TX_TYPE_LABELS,
} from '../../model/labels';
import type {
  BonusAccountStatus,
  BonusTransactionType,
  CardPoolStatus,
  LoyaltyCardStatus,
  LoyaltyCardType,
  LoyaltyProgramModel,
  LoyaltyRuleType,
  ReservationStatus,
} from '../../model/types';

/** Status chips pair colour with a label (never colour alone) per the design system. */

export function BonusStatusChip({ status }: { status: BonusAccountStatus }) {
  const { t } = useTranslation('loyalty');
  const color = status === 'ACTIVE' ? 'success' : status === 'BLOCKED' ? 'warning' : 'default';
  return <Chip size="small" variant="outlined" color={color} label={t(BONUS_STATUS_LABELS[status])} />;
}

export function CardStatusChip({ status }: { status: LoyaltyCardStatus }) {
  const { t } = useTranslation('loyalty');
  const color = status === 'ACTIVE' ? 'success' : status === 'BLOCKED' ? 'warning' : 'default';
  return <Chip size="small" variant="outlined" color={color} label={t(CARD_STATUS_LABELS[status])} />;
}

export function CardTypeChip({ type }: { type: LoyaltyCardType }) {
  const { t } = useTranslation('loyalty');
  return <Chip size="small" variant="outlined" label={t(CARD_TYPE_LABELS[type])} />;
}

export function TxTypeChip({ type }: { type: BonusTransactionType }) {
  const { t } = useTranslation('loyalty');
  const color = type === 'ACCRUAL' ? 'success' : type === 'REDEMPTION' ? 'warning' : 'default';
  return <Chip size="small" variant="outlined" color={color} label={t(TX_TYPE_LABELS[type])} />;
}

export function RuleTypeChip({ type }: { type: LoyaltyRuleType }) {
  const { t } = useTranslation('loyalty');
  return <Chip size="small" variant="outlined" label={t(RULE_TYPE_LABELS[type])} />;
}

export function EnabledChip({ enabled }: { enabled: boolean }) {
  const { t } = useTranslation('loyalty');
  return (
    <Chip
      size="small"
      variant="outlined"
      color={enabled ? 'success' : 'default'}
      label={enabled ? t('chips.enabled') : t('chips.disabled')}
    />
  );
}

export function CardPoolStatusChip({ status }: { status: CardPoolStatus }) {
  const { t } = useTranslation('loyalty');
  return (
    <Chip
      size="small"
      variant="outlined"
      color={status === 'AVAILABLE' ? 'success' : 'default'}
      label={t(CARD_POOL_STATUS_LABELS[status])}
    />
  );
}

export function ProgramModelChip({ model }: { model: LoyaltyProgramModel }) {
  const { t } = useTranslation('loyalty');
  const color = model === 'NONE' ? 'default' : model === 'DISCOUNT' ? 'secondary' : 'primary';
  return <Chip size="small" color={color} label={t(PROGRAM_MODEL_LABELS[model])} />;
}

export function ReservationStatusChip({ status }: { status: ReservationStatus }) {
  const { t } = useTranslation('loyalty');
  const color = status === 'RESERVED' ? 'warning' : status === 'CONFIRMED' ? 'success' : 'default';
  return (
    <Chip size="small" variant="outlined" color={color} label={t(RESERVATION_STATUS_LABELS[status])} />
  );
}
